import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import { useNavigate, useParams } from 'react-router-dom';
import Button from 'carbon-react/lib/components/button';

import { ROUTES, getForecastByIdOnboardingUrl } from '@futrli/shared-services';

import { OnboardingPageTitle } from '../components/onboarding-page-title';
import * as styles from '../forecasting-onboarding.scss';
import { OnboardingPredictionsRouter } from '../components/onboarding-predictions-router';
import {
  BUDGET_ONBOARDING_STEP,
  LS_FORECASTING_ONBOARDING_STEP,
  SCENARIO_ONBOARDING_STEP,
  SCENARIO_WITH_INVOICES_ONBOARDING_STEP
} from '../const';
import additionalControls from '../arrows/activity.png';
import { useAnimationLatency, useExpandAllRows, useOnboardingUrlInfo } from '../utils/hooks';
import { usePredictionsInfoContext } from '../../detail/context/predictions-info-context';

export const OnboardingActivity = () => {
  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisatioId } = useParams();
  const workspaceId = tempWorkspaceId || '';
  const organisationId = tempOrganisatioId || '';
  const { forecast, turnOffPredictionsExplanations, showPredictionsExplanation } = usePredictionsInfoContext();

  const navigate = useNavigate();

  useExpandAllRows();

  useEffect(() => {
    if (showPredictionsExplanation) {
      turnOffPredictionsExplanations?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPredictionsExplanation]);

  const {
    isOnboardingBudgetRoute,
    isOnboardingScenarioRoute,
    isOnboardingScenarioWithInvoicesRoute
  } = useOnboardingUrlInfo();

  const showAnimatedArrow = useAnimationLatency(true);

  const handleNextClick = () => {
    if (!forecast) {
      return;
    }

    let nextStep;
    if (isOnboardingBudgetRoute) {
      nextStep = BUDGET_ONBOARDING_STEP.AUTO_PREDICTIONS_EXPLAINED;
    } else if (isOnboardingScenarioRoute) {
      nextStep = SCENARIO_ONBOARDING_STEP.AUTO_PREDICTIONS_EXPLAINED;
    } else {
      nextStep = SCENARIO_WITH_INVOICES_ONBOARDING_STEP.AUTO_PREDICTIONS_EXPLAINED;
    }
    navigate(
      `${getForecastByIdOnboardingUrl(
        workspaceId,
        organisationId,
        forecast.id,
        isOnboardingBudgetRoute,
        isOnboardingScenarioWithInvoicesRoute
      )}/${nextStep}${ROUTES.SALES}${location.search}`
    );

    localStorage.setItem(LS_FORECASTING_ONBOARDING_STEP, nextStep);
  };

  return (
    <div className={styles.contentContainer} style={{ position: 'relative' }}>
      <img
        alt="onboarding-arrow"
        src={additionalControls}
        style={{ position: 'absolute', left: 37, top: 150, zIndex: 5 }}
        className={classNames(styles.fadeInContainer, {
          [styles.fadeIn]: showAnimatedArrow
        })}
      />
      <OnboardingPageTitle
        title={
          <FormattedMessage
            id="onboarding.easy_reviewing"
            defaultMessage="For easy reviewing, auto-predictions have traffic light confidence indicators..."
          />
        }
        description={
          <FormattedMessage
            id="onboarding.predict_data_for_accounts"
            defaultMessage="Review accounts that are {red} first. Our confidence in the prediction is low due to the lack of patterns found in invoice, bill or journal activity. {amber} and {green} we are more confident with at this time."
            values={{
              green: (
                <span style={{ color: '#3A7E5F', fontWeight: 500 }}>
                  <FormattedMessage id="common.green" defaultMessage="green" />
                </span>
              ),
              amber: (
                <span style={{ color: '#A35F1F', fontWeight: 500 }}>
                  <FormattedMessage id="common.amber" defaultMessage="amber" />
                </span>
              ),
              red: (
                <span style={{ color: '#CE3B4C', fontWeight: 500 }}>
                  <FormattedMessage id="common.red" defaultMessage="red" />
                </span>
              )
            }}
          />
        }
      />
      <Button mb="30px" size="large" buttonType="primary" onClick={handleNextClick}>
        <FormattedMessage id="common.next" defaultMessage="Next" />
      </Button>
      <OnboardingPredictionsRouter />
    </div>
  );
};
